import React, { useEffect, useState, useRef} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import modelViewerStyle from "./modelViewerStyle";
import { IconButton, Typography, Popover, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloseFullscreenIcon from "../../assets/images/fullscreen-exit.svg";
import setupLights from "./components/LightsSetup";
import {loadModels} from "../../utils/loadModels";
import initializeViewer from "./components/initializeViewer";
import setupSAOShadow from "./components/setupSAOShadow";
import setupGrid from "./components/setupGrid.js";
import ZoomControl from "./components/ZoomControl";
import NorthControl from "./components/NorthControl";
import CameraControl from "./components/CameraControl";
import TopBar from "./components/TopBar";
import treeViewSetup from "./components/treeViewSetup.js";
import entityInteraction from "./components/entityInteraction.js";
import Feedback from "./components/Feedback";


const ModelViewer = () => {
  const isInitialized = useRef(false);
  const classes = modelViewerStyle(); // Use the styles
  const [entityData, setEntityData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null); // Store the reference for the clicked element
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 }); // For popover positioning
  const viewerRef= useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [treeViewPlugin, setTreeViewPlugin] = useState(null);
  const treeViewRef = useRef(null);
  const [isTreeViewVisible, setTreeViewVisible] = useState(false);

  const [isInfoMode, setIsInfoMode] = useState(false);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  useEffect(() => {
    if (isInitialized.current) {
      console.warn("⚠️ Viewer already initialized, skipping...");
      return;
    }
    isInitialized.current = true;
    const initialize = async () => {

      //viewer initialization
      const viewer = await initializeViewer(viewerRef, setEntityData, setPopoverPosition, setAnchorEl);
      console.log("viewer is ready");
      window.viewer = viewer;

      //Add lights
      setupLights(viewer);

      //Add SAO and Shadow
      setupSAOShadow(viewer);

      // Add grid
      const { gridGeometry, gridMesh } = setupGrid(viewer);

      //load modelsc
      await loadModels(viewer);
      console.log("models loaded");

      //TreeView
      let treeView = null;

      treeView = treeViewSetup(viewer);
      if (treeView) {
        treeViewRef.current = treeView;
      }

    return () => {
      // Cleanup
      gridGeometry.destroy();
      gridMesh.destroy();
      viewer.destroy();
      treeView.destroy();
    };

  };
  initialize();

}, []);

useEffect(()=>{
    if (window.viewer) {
      entityInteraction(window.viewer, isInfoMode, setEntityData, setPopoverPosition, setAnchorEl);
    }
}, [isInfoMode]);

  const toggleInfoMode = () => {
    setIsInfoMode(!isInfoMode);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const toggleTreeView = () => {
    if (isFeedbackOpen) {
      setIsFeedbackOpen(false);
      console.log("✅ Feedback closed before opening TreeView.");
    }
    if (!treeViewRef.current) {
      console.error("❌ TreeViewPlugin is not initialized or containerElement is undefined.");
      return;
    }

  const treeViewContainer = document.getElementById("myTreeView");
  console.log("treeViewContainer", treeViewContainer);
  if (!treeViewContainer) {
    console.error("❌ TreeView container element is missing in the DOM.");
    return;
}

// ✅ Toggle visibility
if (treeViewContainer.classList.contains(classes.treeViewHidden)) {
    treeViewContainer.classList.remove(classes.treeViewHidden);
    treeViewContainer.classList.add(classes.treeViewVisible);
    setTreeViewVisible(true);
} else {
    treeViewContainer.classList.remove(classes.treeViewVisible);
    treeViewContainer.classList.add(classes.treeViewHidden);
    setTreeViewVisible(false);
}

};
const toggleFeedback = () => {
  // Ensure TreeView is closed before opening Feedback
  const treeViewContainer = document.getElementById("myTreeView");
  if (treeViewContainer && treeViewContainer.classList.contains(classes.treeViewVisible)) {
      treeViewContainer.classList.remove(classes.treeViewVisible);
      treeViewContainer.classList.add(classes.treeViewHidden);
      setTreeViewVisible(false);
      console.log("✅ TreeView closed before opening Feedback.");
  }

  setIsFeedbackOpen(!isFeedbackOpen);
};

const closeFeedback = () => setIsFeedbackOpen(false);

  return (
    <div className={classes.container}>
      {/*TopBar*/}
      <TopBar 
              toggleTreeView={toggleTreeView} 
              toggleInfoMode={toggleInfoMode}
              isInfoMode={isInfoMode}
              toggleFeedback={toggleFeedback}
              isTreeViewVisible= {isTreeViewVisible}
              isFeedbackOpen={isFeedbackOpen}
      />

      {/* TreeView Container - Positioned below TopBar */}
      <div id="myTreeView" className={isTreeViewVisible ? classes.treeViewVisible : classes.treeViewHidden} />

      {/* Feedback Panel */}
      <Feedback isOpen={isFeedbackOpen} closeFeedback={closeFeedback} />

      {/* Show Exit Fullscreen Button ONLY in /xeokit-viewer */}
      {location.pathname === "/xeokit-viewer" && (
        <IconButton
            className={classes.exitFullscreenButton}
            onClick={() => {
              navigate("/"); // Step 1: Navigate to homepage
              setTimeout(() => {
                const viewerSection = document.getElementById("viewerToggle");
                if (viewerSection) {
                  viewerSection.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }, 300); // Delay to allow navigation to complete
            }}>
              <img 
                src={CloseFullscreenIcon} 
                alt="Exit Fullscreen"
                className={classes.exitFullscreenButton}
              />
            </IconButton>
      )}
      <ZoomControl viewerRef = {viewerRef} />
      <NorthControl viewerRef= {viewerRef}/>
      <CameraControl viewerRef={viewerRef}/>
      <canvas id="myViewerCanvas" className={classes.canvas}></canvas>

       {/* Popover for entity data */}
       <Popover
        open={Boolean(anchorEl)}
        anchorReference="anchorPosition"
        anchorPosition={popoverPosition}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            minWidth: 280,
            minHeight: 180,
            margin: '0',
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.39)",
            borderRadius: '23px',
          },
        }}
      >
        <Box className={classes.modal}>
          <div className={classes.modalHeader}>
            <Typography className={classes.modalTitle}>Entity Details</Typography>
            <IconButton className={classes.modalCloseButton} onClick={handleClosePopover}>
                <CloseIcon />
            </IconButton>
          </div>
          {entityData?.metadata && (
            <>
            {entityData.metadata.id && (
              <Typography className={classes.modalAttribute}>
                <strong>ID:</strong> {entityData.metadata.id}
              </Typography>
            )}
            {entityData.metadata.name && (
              <Typography className={classes.modalAttribute}>
                <strong>Name:</strong> {entityData.metadata.name}
              </Typography>
            )}
            {entityData.metadata.type && (
              <Typography className={classes.modalAttribute}>
                <strong>Type:</strong> {entityData.metadata.type}
              </Typography>
            )}
            {entityData.metadata.parentName && (
              <Typography className={classes.modalAttribute}>
                <strong>Parent Name:</strong> {entityData.metadata.parentName}
              </Typography>
            )}
            </>
          )}
        </Box>
      </Popover>
    </div>
  );
};

export default ModelViewer;